<template>
  <div
    :class="`lowcode-render-container ${
      renderType === 'preview' ? 'preview' : ''
    }`"
    v-if="currentPageDsl.pageId">
    <component
      v-for="item in renderDslList"
      :key="item.cid"
      :data="item"
      :is="components[`${item.type}Component`]"></component>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { showToast } from 'vant'
import useWechat from '@/hooks/useWechat'
import useQrCode from '@/hooks/useQrCode'
import useTrack from '@/hooks/useTrack'
import useCrowdPackageAccess from '@/hooks/useCrowdPackageAccess'
import { setTitle } from '@/utils/index'

const route = useRoute()
const store = useStore()
const { wechatAuth } = useWechat()
const { fetchSamePeerStatus } = useQrCode()
const { track } = useTrack()
const { hasCrowdPackageAccess, initializeCrowdPackageAccess } =
  useCrowdPackageAccess()
const currentPageDsl = computed(() => store.state.currentPageDsl)
const commonDsl = computed(() => store.state.dslData.commonDsl)
const renderType = computed(() => store.state.renderType)
const { pageType, id, subId } = route.params
const components = ref({})
const userInfo = computed(() => store.state.userInfo)
const renderDslList = computed(() => {
  const list = currentPageDsl.value?.dslContent || []
  if (pageType !== 'wxBasic') return list
  return list.filter((item) => !item.popupParentCid)
})
const shouldLockScroll = computed(() =>
  renderDslList.value.some(
    (item) =>
      item.type === 'maskComponent' &&
      item.maskType === 'bottomSheet' &&
      !hasCrowdPackageAccess(
        item.visibleCrowdPackageIds,
        item.crowdMatchParamType,
        commonDsl.value?.webPageType,
      ),
  ),
)

const originalHtmlOverflow = document.documentElement.style.overflow
const originalBodyOverflow = document.body.style.overflow

watch(
  shouldLockScroll,
  (locked) => {
    document.documentElement.style.overflow = locked
      ? 'hidden'
      : originalHtmlOverflow
    document.body.style.overflow = locked ? 'hidden' : originalBodyOverflow
  },
  { immediate: true },
)

const timecount = ref(0)
let timer = null

// 页面滑动触底
const scrollTrack = () => {
  let hitted = false
  document.addEventListener('scroll', () => {
    if (hitted) return
    const scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop
    const scrollHeight =
      document.documentElement.scrollHeight || document.body.scrollHeight
    const clientHeight =
      document.documentElement.clientHeight || window.innerHeight
    if (scrollTop + clientHeight + 50 >= scrollHeight) {
      hitted = true
      track('hit_ad_bottom', {
        is_bottom: '是'
      })
    }
  })
}

// 页面停留计时
const initTimer = () => {
  if (timer) clearInterval(timer)
  timer = setInterval(() => {
    timecount.value = timecount.value + 1
  }, 1000)

  // 离开页面埋点
  window.addEventListener('pagehide', () => {
    if (timer) clearInterval(timer)
    if (timecount.value >= 3)
      track('exit_ad_page', { stay_time: timecount.value })
  })
  if (commonDsl.value?.webPageType == 5) {
    // 兼容左滑页面没有隐藏浏览器缓存页面触发不了pagehide
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          if (timer) clearInterval(timer)
        if (timecount.value >= 3)
          track('exit_ad_page', { stay_time: timecount.value })
        }
    });
  }
}

// 根据页面type渲染某套组件（推广/物料/运营）
onMounted(async () => {
  // vaster预览模式
  if (id === 'preview' || route.query?.preview)
    store.commit('setRenderType', { renderType: 'preview' })
  if (id === 'preview') {
    // store.commit('setRenderType', { renderType: 'preview' })
    window.addEventListener(
      'message',
      (msg) => {
        if (msg.data?.dslContent) {
          if (msg.data.pageSize) {
            store.commit('setPreviewPageSize', msg.data.pageSize)
          }
          store.commit('setCurrentPageDsl', {
            pageId: 'preview',
            dslContent: msg.data.dslContent,
          })
          initializeCrowdPackageAccess(
            msg.data.dslContent,
            commonDsl.value?.webPageType,
          )
        }
      },
      false
    )
  } else {
    // 接口获取页面信息
    try {
      await store.dispatch('fetchPageInfo', {
        key: route.query.pageKey || route.query.key || ''
      })
      // 锁定当前展示页面
      await store.commit('getPageDslByPageId', { id, subId })
      await initializeCrowdPackageAccess(
        renderDslList.value,
        commonDsl.value?.webPageType,
      )
    } catch (err) {
      showToast('获取页面数据失败')
      return
    }
    // 更改页面标题
    if (currentPageDsl.value?.pageTitle) setTitle(currentPageDsl.value?.pageTitle)
  }

  // 页面滑动触底埋点
  scrollTrack()
  // 初始化页面计时器
  initTimer()

  // 微信授权
  if (commonDsl.value?.env === 0 && commonDsl.value?.needAuth) {
    // 推广页面
    await wechatAuth()
  }else if(commonDsl.value?.currentEnv === 'wechat' && commonDsl.value?.needAuth) {
    // 运营页面
    await wechatAuth()
    if(!window.location.search.includes("xinstall_unionid") || !window.location.search.includes("xinstall_username")) {
      window.location.href =  `${window.location.href}&xinstall_unionid=${userInfo.value?.unionid || ''}&xinstall_username=${userInfo.value?.username || userInfo.value?.userData?.username || ''}`
    }
  }

  // 进入页面埋点
  // track('enter_ad_page', {})
  if (!window.location.search.includes("xinstall_unionid")) {
    track('enter_ad_page', {})
  }

  // 防同行逻辑 将信息保存到本地中
  await fetchSamePeerStatus()

  // 组件引入
  components.value = (
    await import(`@/components/${pageType}Components/index`)
  ).default
})

onUnmounted(() => {
  // 页面退出时清除计时器
  if (timer) clearInterval(timer)
  document.documentElement.style.overflow = originalHtmlOverflow
  document.body.style.overflow = originalBodyOverflow
})

watch(
  () => route.params,
  async (val) => {
    const { id, subId } = val
    store.commit('getPageDslByPageId', { id, subId })
    await initializeCrowdPackageAccess(
      renderDslList.value,
      commonDsl.value?.webPageType,
    )
    // 初始化页面计时器
    initTimer()
  }
)

watch(
  () => route.query.token,
  async () => {
    await initializeCrowdPackageAccess(
      renderDslList.value,
      commonDsl.value?.webPageType,
    )
  },
)
</script>
